import { Link } from 'react-router'
import { Card } from '@carouselgg/nexus/card'
import { Avatar } from '@carouselgg/nexus/avatar'
import { Text } from '@carouselgg/nexus/text'
import { Badge } from '@carouselgg/nexus/badge'
import { Grid } from '@carouselgg/nexus/grid'
import { useAuthStore } from '@carouselgg/auth/store'
import { useMyOrgs } from '@carouselgg/auth/use-my-orgs'
import type { MyOrg } from '@carouselgg/auth/use-my-orgs'
import styles from './MyOrgsSection.module.css'

interface MyOrgsSectionProps {
  /** Filter to a single org type. Omit to show all of the user's orgs. */
  type?: string
  title?: string
}

/**
 * Your organizations as cards linking to each one's page, for discovery
 * surfaces. Managing and leaving them lives in your account (Teams & clubs).
 */
export function MyOrgsSection({ type, title = 'My Organizations' }: MyOrgsSectionProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const allOrgs = useMyOrgs(isAuthenticated)
  const orgs = type ? allOrgs.filter((o) => o.type === type) : allOrgs

  if (!isAuthenticated || orgs.length === 0) return null

  return (
    <div className={styles.wrapper}>
      <Text as="h2" size="lg" weight="semibold" className={styles.heading}>
        {title}
      </Text>
      <Grid cols={3} colsMd={2} colsSm={1} gap="md">
        {orgs.map((org) => <MyOrgCard key={org.slug} org={org} />)}
      </Grid>
    </div>
  )
}

function MyOrgCard({ org }: { org: MyOrg }) {
  return (
    // The Link is the grid item and stretches, but the Card inside it sized
    // to its own content — so an org whose name wrapped to two lines made a
    // taller card than its neighbours. Both have to fill the track.
    <Link to={`/org/${org.slug}`} className={styles.cardWrapper}>
      <Card interactive accent={org.brandColor || 'var(--nexus-purple)'} className={styles.fill}>
        <div className={styles.card}>
          <Avatar src={org.logoUrl} fallback={org.name.charAt(0)} size="md" />
          <div className={styles.info}>
            <Text size="base" weight="semibold">{org.name}</Text>
            <Badge size="sm">{org.role.replace(/_/g, ' ')}</Badge>
          </div>
        </div>
      </Card>
    </Link>
  )
}
